/* Adapted from Safety Wholesale v1.5.16. IC catalogue prices include VAT. */
(() => {
  const initialise = (form) => {
    if (form.dataset.icOrderInitialised === 'true') return;
    const section = form.closest('[data-section-type="product"]');
    if (!section) return;
    const panel = form.querySelector('[data-ic-order-panel]');
    const summary = form.querySelector('[data-ic-order-summary]');
    if (!panel || !summary) return;
    const rows = [...panel.querySelectorAll('[data-ic-order-row]')];
    const controls = panel.querySelector('[data-ic-order-controls]');
    const submit = form.querySelector('[data-ic-order-submit]');
    const errorBox = form.querySelector('[data-ic-order-error]');
    const review = form.querySelector('[data-ic-order-review]');
    if (rows.length !== Number(panel.dataset.variantCount)) return;
    const currency = summary.dataset.currency || 'GBP';
    const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const money = (cents) => formatter.format(cents / 100);
    let busy = false;
    let reviewRequired = false;
    let completed = false;
    const modes = form.querySelector('[data-ic-order-modes]');
    const modeButtons = [...form.querySelectorAll('[data-ic-order-mode]')];
    const modeNote = form.querySelector('[data-ic-order-mode-note]');
    const payment = form.querySelector('[data-ic-order-standard-payment]');
    const paymentPosition = document.createComment('Original purchase button position');
    if (payment) payment.before(paymentPosition);
    const originalNoValidate = form.noValidate;
    const nativeNodes = [...form.querySelectorAll('[data-ic-order-native], [data-ic-subscription-slot], [data-ic-order-standard-payment] > .product-form__add-button, [data-ic-order-standard-payment] > .shopify-payment-button')];
    const nativeInert = new Map(nativeNodes.map(node => [node, node.inert]));
    const nativeSubmitButtons = [...form.querySelectorAll('[data-ic-order-standard-payment] > button[type="submit"]')];
    const promise = section.querySelector('[data-ic-delivery]');
    let nativeDeliveryHidden = promise?.hidden;
    const multipleActive = () => form.dataset.icOrderReady === 'true';

    const optionalLimit = (value) => value === '' || value == null ? Infinity : Number(value);
    const stockLimit = (data) => Math.min(optionalLimit(data.max), optionalLimit(data.stock));
    const quantityError = (quantity, data, allowZero = true) => {
      if (!Number.isSafeInteger(quantity) || quantity < 0) return 'Enter a whole number of 0 or more.';
      if (allowZero && quantity === 0) return '';
      const minimum = Math.max(1, Number(data.min || 1));
      const step = Math.max(1, Number(data.step || 1));
      if (quantity < minimum) return `Minimum quantity is ${minimum}.`;
      if ((quantity - minimum) % step !== 0) return `Order in increments of ${step}, starting at ${minimum}.`;
      const maximum = stockLimit(data);
      if (quantity > maximum) return `A maximum of ${Math.max(0, maximum)} can be ordered. Availability is checked again in your basket.`;
      return '';
    };

    const readRows = () => rows.map((row) => {
      const input = row.querySelector('[data-ic-order-quantity]');
      const quantity = row.dataset.available === 'true' ? Number(input.value || 0) : 0;
      const message = input.validity.badInput ? 'Enter a whole number of 0 or more.' : quantityError(quantity, row.dataset);
      const rowError = row.querySelector('[data-ic-order-row-error]');
      input.setCustomValidity(message);
      input.setAttribute('aria-invalid', String(Boolean(message)));
      rowError.textContent = message;
      rowError.hidden = !message;
      row.classList.toggle('is-selected', quantity > 0 && !message);
      const minus = row.querySelector('[data-ic-order-minus]');
      const plus = row.querySelector('[data-ic-order-plus]');
      minus.disabled = row.dataset.available !== 'true' || quantity === 0;
      const next = quantity === 0 ? Number(row.dataset.min || 1) : quantity + Number(row.dataset.step || 1);
      plus.disabled = row.dataset.available !== 'true' || next > stockLimit(row.dataset);
      return { id: Number(row.dataset.variantId), quantity, price: Number(row.dataset.unitPrice), taxable: row.dataset.taxable === 'true', vatRate: Number(row.dataset.vatRate ?? 20), title: row.dataset.variantTitle, rules: row.dataset, input, message };
    });

    const readAddons = () => [...form.querySelectorAll('[data-ic-accessory-checkbox]:checked:not(:disabled)')].map(choice => {
      const card = choice.closest('[data-ic-accessory-card]');
      const input = card.querySelector('[data-ic-accessory-quantity]');
      const data = card.dataset;
      const rules = { min: data.orderMin, step: data.orderStep, max: data.orderMax, stock: data.orderStock };
      const quantity = Number(input?.value || 0);
      return {
        id: Number(choice.dataset.variantId), quantity, price: Number(data.incCents || 0),
        taxable: data.taxable === 'true', vatRate: Number(data.vatRate ?? 20),
        title: choice.dataset.accessoryTitle || 'Optional extra', rules, input,
        message: input?.validity.badInput ? 'Enter a whole number.' : quantityError(quantity, rules, false)
      };
    });

    const showError = (message, focus = false) => {
      errorBox.textContent = message;
      errorBox.hidden = !message;
      if (message && focus) errorBox.focus();
    };

    const refresh = () => {
      if (!multipleActive()) return;
      const components = readRows();
      const selected = components.filter((line) => !line.message && line.quantity > 0);
      const componentQuantity = selected.reduce((total, line) => total + line.quantity, 0);
      const addons = readAddons();
      const validAddons = addons.filter((line) => !line.message);
      const lines = [...selected, ...validAddons];
      const gross = lines.reduce((total, line) => total + line.price * line.quantity, 0);
      const net = lines.reduce((total, line) => total + Math.round(line.price * line.quantity / (line.taxable ? 1 + line.vatRate / 100 : 1)), 0);
      const errors = [...components, ...addons].filter((line) => line.message);
      form.querySelector('[data-ic-order-count]').textContent = componentQuantity > 0
        ? `${componentQuantity} ${componentQuantity === 1 ? 'item' : 'items'} · ${selected.length} ${selected.length === 1 ? 'variation' : 'variations'}`
        : 'No items selected';
      form.querySelector('[data-ic-order-extras]').hidden = addons.length === 0;
      form.querySelector('[data-ic-order-total-ex]').textContent = money(net);
      form.querySelector('[data-ic-order-total-inc]').textContent = money(gross);
      submit.disabled = busy || reviewRequired || componentQuantity === 0 || errors.length > 0;
      if (!busy) submit.textContent = 'Add selected items to basket →';
      const invalidAddon = addons.find(line => line.message);
      if (invalidAddon && !busy && !reviewRequired) showError(`${invalidAddon.title}: ${invalidAddon.message}`);
      if (promise) promise.hidden = selected.length === 0 || errors.length > 0 || selected.some(line =>
        line.rules.inventoryTracked !== 'true' || Number(line.rules.inventoryQuantity) < line.quantity);
      return { components, selected, addons, componentQuantity, errors };
    };

    const setBusy = (value) => {
      busy = value;
      controls.disabled = value || !multipleActive();
      modeButtons.forEach(button => { button.disabled = value || reviewRequired; });
      form.querySelectorAll('[data-ic-accessories]').forEach((picker) => { picker.inert = value; });
      submit.setAttribute('aria-busy', String(value));
      refresh();
      if (value) submit.textContent = 'Adding your selection…';
    };

    const setMode = (multiple) => {
      if (busy || reviewRequired || form.querySelector('[data-ic-accessories][data-ic-busy="true"]')) return;
      form.dataset.icOrderReady = String(multiple);
      form.noValidate = multiple || originalNoValidate;
      controls.disabled = !multiple;
      panel.hidden = !multiple;
      summary.hidden = !multiple;
      // Only the active purchase flow participates in implicit keyboard submission.
      submit.type = multiple ? 'submit' : 'button';
      submit.disabled = !multiple;
      nativeSubmitButtons.forEach(button => { button.type = multiple ? 'button' : 'submit'; });
      nativeNodes.forEach(node => { node.inert = multiple || nativeInert.get(node); });
      modeButtons.forEach(button => button.setAttribute('aria-pressed', String((button.dataset.icOrderMode === 'multiple') === multiple)));
      if (modeNote) modeNote.hidden = multiple;
      if (payment) {
        // Keep quantity-break app insertion containers above extras in multi-buy.
        // Restore the original form order when Appstle handles a single variation.
        if (multiple) form.insertBefore(payment, form.querySelector('[data-ic-accessories]') || summary);
        else paymentPosition.after(payment);
      }
      if (multiple) refresh();
      else if (promise) promise.hidden = nativeDeliveryHidden;
      // Refresh the retained add-button label if extras changed in the other mode.
      form.querySelector('[data-ic-accessory-checkbox]')?.dispatchEvent(new Event('change', { bubbles: true }));
    };
    modeButtons.forEach(button => button.addEventListener('click', () => setMode(button.dataset.icOrderMode === 'multiple')));
    section.addEventListener('variant:changed', () => {
      // The existing document handler updates VAT and delivery for the native selector.
      // Read its result after bubbling, then restore the multi-selection delivery state.
      queueMicrotask(() => {
        nativeDeliveryHidden = promise?.hidden;
        if (multipleActive()) refresh();
      });
    });

    const fetchJson = async (url, options = {}) => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        const data = await response.json();
        return { response, data };
      } finally { window.clearTimeout(timeout); }
    };

    const cartQuantities = (cart) => {
      if (!Array.isArray(cart?.items)) throw new Error('Unable to read the basket.');
      return cart.items.reduce((counts, item) => {
        const id = String(item.variant_id);
        counts[id] = (counts[id] || 0) + Number(item.quantity || 0);
        return counts;
      }, {});
    };

    rows.forEach((row) => {
      const input = row.querySelector('[data-ic-order-quantity]');
      const step = (direction) => {
        if (!multipleActive() || busy || reviewRequired || row.dataset.available !== 'true') return;
        const current = Math.max(0, Math.floor(Number(input.value) || 0));
        const min = Number(row.dataset.min || 1);
        const increment = Number(row.dataset.step || 1);
        const next = direction > 0 ? (current < min ? min : current + increment) : (current <= min ? 0 : Math.max(min, current - increment));
        if (direction > 0 && next > stockLimit(row.dataset)) return;
        input.value = String(next);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      };
      row.querySelector('[data-ic-order-minus]').addEventListener('click', () => step(-1));
      row.querySelector('[data-ic-order-plus]').addEventListener('click', () => step(1));
    });

    const onChange = (event) => {
      if (!multipleActive() || !event.target.closest('[data-ic-order-row], [data-ic-accessories]')) return;
      if (!busy && !reviewRequired) showError('');
      refresh();
    };
    form.addEventListener('ic:accessories-updated', () => {
      if (!multipleActive()) return;
      if (!busy && !reviewRequired) showError('');
      refresh();
    });
    section.addEventListener('input', onChange);
    section.addEventListener('change', onChange);
    section.addEventListener('click', (event) => {
      if (event.target.closest('[data-ic-accessory-decrease], [data-ic-accessory-increase]')) onChange(event);
    });

    form.addEventListener('keydown', event => {
      if (!multipleActive() || event.key !== 'Enter' || !event.target.matches('[data-ic-order-quantity], [data-ic-accessory-quantity]')) return;
      event.preventDefault();
      form.requestSubmit(submit);
    });

    form.addEventListener('submit', async (event) => {
      if (!multipleActive()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || reviewRequired) return;
      const state = refresh();
      if (state.errors.length) {
        const invalid = state.errors[0];
        showError(`${invalid.title}: ${invalid.message}`, true);
        invalid.input?.focus();
        return;
      }
      if (state.componentQuantity === 0) {
        showError('Enter a quantity for at least one variation.', true);
        return;
      }
      const properties = {};
      for (const [name, value] of new FormData(form).entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      }
      const hasProperties = Object.keys(properties).length > 0;
      const lines = [...state.selected, ...state.addons];
      const itemsById = new Map();
      lines.forEach((line, index) => {
        const main = index < state.selected.length;
        const key = String(line.id) + (main && hasProperties ? ':main' : '');
        const existing = itemsById.get(key);
        itemsById.set(key, { id: line.id, quantity: (existing?.quantity || 0) + line.quantity,
          ...(main && hasProperties ? { properties } : {}) });
      });
      const items = [...itemsById.values()];
      if (items.length > 250 || items.some((item) => !Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0)) {
        showError('This selection could not be prepared. Please contact Customer Service.', true);
        return;
      }
      showError('');
      review.hidden = true;
      setBusy(true);
      let before;
      let addAttempted = false;
      try {
        const cartResult = await fetchJson(summary.dataset.cartJsonUrl);
        if (!cartResult.response.ok) throw new Error('Your basket could not be checked. No new request was sent. Please try again.');
        before = cartQuantities(cartResult.data);
        for (const line of lines) {
          const alreadyInCart = before[String(line.id)] || 0;
          const combined = alreadyInCart + items.filter(item => item.id === line.id).reduce((sum, item) => sum + item.quantity, 0);
          if (combined > stockLimit(line.rules)) throw new Error(`${line.title}: ${alreadyInCart} already in your basket. Reduce the new quantity or review your basket.`);
        }
        addAttempted = true;
        const added = await fetchJson(summary.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!added.response.ok) {
          const message = typeof added.data.description === 'string' ? added.data.description : 'Shopify could not add the complete selection.';
          const rejection = new Error(message);
          rejection.uncertain = added.response.status >= 500;
          throw rejection;
        }
        // A successful request is never retried. The basket shows final stock,
        // taxes and automatic discounts, including the existing discount app.
        reviewRequired = true;
        completed = true;
        form.querySelectorAll('[data-ic-accessory-checkbox]:checked').forEach(input => { input.checked = false; input.dispatchEvent(new Event('change', { bubbles: true })); });
        panel.querySelectorAll('[data-ic-order-quantity]').forEach(input => { input.value = '0'; });
        window.location.assign(summary.dataset.cartUrl);
      } catch (error) {
        let changedOrUnknown = addAttempted;
        if (addAttempted && before) {
          try {
            const after = await fetchJson(summary.dataset.cartJsonUrl);
            if (after.response.ok) {
              const counts = cartQuantities(after.data);
              changedOrUnknown = items.some((item) => (counts[String(item.id)] || 0) !== (before[String(item.id)] || 0));
              // A transport failure can still complete after this check.
              if (error.uncertain || error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError) changedOrUnknown = true;
            }
          } catch (ignored) { changedOrUnknown = true; }
        }
        reviewRequired = changedOrUnknown;
        const detail = error.name === 'AbortError' ? 'The request timed out.' : error.message || 'There was a connection problem.';
        showError(changedOrUnknown ? `${detail} Some items may already have been added. Review your basket before adding again.` : `${detail} Please check the quantities and try again.`, true);
        review.hidden = false;
        setBusy(false);
      }
    }, true);

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      if (completed) { completed = false; reviewRequired = false; }
      if (!reviewRequired) setBusy(false);
    });
    // Promote the existing form only after every required node and row is present.
    // Without JavaScript, or after a failed initialisation, the usual selector remains usable.
    form.dataset.icOrderInitialised = 'true';
    // Preserve an explicitly linked subscription; otherwise start with one-time quantities.
    setMode(!modes || !new URL(window.location.href).searchParams.get('selling_plan'));
    if (modes) modes.hidden = false;
  };

  document.querySelectorAll('form[data-ic-order-enabled="true"]').forEach(initialise);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('form[data-ic-order-enabled="true"]').forEach(initialise);
  });
})();
